import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { App } from './app/App';
import './styles/globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A POS is a live view of a shop — refetch on focus, but don't hammer
      // the server while a cashier is mid-sale.
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      // React Query's default `networkMode: 'online'` PAUSES a query the
      // moment its own `onlineManager` thinks the browser is offline —
      // the fetch never runs at all, so it never throws, so it never
      // becomes `isError`. That silently breaks every "try the live call,
      // catch a real network failure, fall back" path this app depends on
      // (lib/offline-queue.ts's `isNetworkFailure`, and README.md Phase
      // 21's cold-start offline selling) — found live, via the offline
      // catalog search fallback simply never firing under a real
      // `context.setOffline(true)` test. `'always'` makes React Query get
      // out of the way and let `fetch()` itself decide, which is exactly
      // what those call sites already assume is happening.
      networkMode: 'always',
    },
    mutations: {
      networkMode: 'always',
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('Missing #root');

createRoot(container).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      <Toaster position="top-right" richColors closeButton />
    </QueryClientProvider>
  </React.StrictMode>,
);
